import React, { Component } from 'react';
import axios from "axios";
import moment from "moment";
import _, { forEach } from 'lodash';
import mapJobTypeFields from "../../helpers/mapJobTypeFields";
import formatLink from "../../utils/formatLink";
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import Alert from "../../components/Alert/Alert";
import css from './FieldJob.module.scss';
import Downshift from 'downshift';


/**
 * @class
 *
 * FieldJob Page -- a list of form fields for individual field jobs.
 *
 * This is probably the most complex pages due to the complex rules/conditions for dynamically generating fields, hiding/showing dependant fields.
 * After fetching initial data, we map the fields via `mapJobTypeFields.js`. The fields we get back (both displayed and editable fields) depend
 * on the job type (e.g. "Electrical", "Service Call", "Site Survey", etc.).
 *
 * Due to the to the complexity of the forms (e.g. dynamically generated form fields), this page DOES NOT use Formik to handle forms and form validation.
 */
const FieldJob = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    fieldJobId = this.props.match.params.field_job_id;
    metaDataJobFields = null;
    schedulableId = null;
    pickFields = [];
    initialData = {}; // this remains unchanged and is only used during saving -- to compare original page load data to see which fields/values have been changed; it never changes after initializing

    SF_DATE_FORMAT = "YYYY-MM-DDT00:00:00Z";
    SF_TIME_FORMAT = "HH:mm:SSSZ";
    SF_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
    SC_DATE_FORMAT = "YYYY-MM-DD";
    FIELD_EXPLANATION_MESSAGE = 'Please explain in Job Notes.';

    postRequestFindElectricians = {
        fields: {
            Id: 1,
            Name: 1,
            FirstName: 1,
            LastName: 1
        },
        where: {
            Active__c: true,
            Job_Department__c: {$in: ['Electrician', 'Installation']}
        },
        orderBy: {
            Name: 1
        }
    };

    postRequestFindEquipment = {
        limit: 100,
        skip: 0,
        fields :[ "Id", "Name"],
        where: {
            Type_of_Equipment__c: "Module"
        }
    };

    // primary records:
    postRequestFields = {
        "limit": 1,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "CreatedDate",
            "Completed_By__c",
            "Completed_By__r.Name",
            "Clocked_Start__c",
            "Clocked_End__c",
            "Dropbox_Link__c",
            "RecordTypeId",
            "RecordType.Name",
            "Customer_was_home__c",
            "Site_Survey_Outcome__c",
            "What_Was_Partially_Completed__c",
            "What_Was_Fully_Completed__c",
            "What_Was_The_Reason_For_Cancelling__c",
            "Ground_work_complete__c",
            "Customer_knows_how_to_turn_on_system__c",
            "Any_broken_tiles__c",
            "Follow_up_needed_picklist__c",
            "Has_the_problem_been_fixed__c",
            "Roof_Work_Complete__c",
            "Job_Date__c",
            "Job_card_uploaded__c",
            "Inverter_serial_pics_uploaded__c",
            "Job_Subtype__c",
            "Number_of_Broken_Tiles__c",
            "Opportunity_ID__c",
            "Scheduled_Time_Slot__c",
            "Survey_Additions__c",
            "Status__c",
            "Type__c",
            "Schedulable__c",
            "Schedulable__r.Customer_Name__c",
            "Schedulable__r.Site_Survey_Completed__c",
            "Schedulable__r.Site_Survey_Notes__c",
            "Schedulable__r.Schedulable_Complete__c",
            "Schedulable__r.Completion_Notes__c",
            "Schedulable__r.Jobsite_Photos__c",
            "Schedulable__r.NEM_Signed_Date__c",
            "Schedulable__r.SS_Completed_Opp__c",
            "Schedulable__r.Inspection_Type__c",
            "Schedulable__r.Assigned_Crew__c",
            "Waste_Management_Method__c",
            "Trench_Distance__c",
            "Select_materials_in_the_trench_path__c",
            "Is_There_Existing_Solar__c",
            "Electrical_Work_Complete__c",
            "Inspection_Outcome__c",
            "Install_Outcome__c",
            "Does_the_work_match_the_plans__c",
            "Tie_in_completed__c",
            "Ready_for_final_inspection__c",
            "All_required_job_pictures_uploaded__c",
            "System_fully_paired_and_pic_uploaded__c",
            "Monitoring_fully_setup_and_pic_uploaded__c",
            "Did_the_customer_sign_the_NEM_form__c",
            "Design_revision_needed__c",
            "Permit_revision_needed__c",
            "Job_Notes__c",
            "Customer_verbally_approved_layout__c",
            "hundred_ready_for_final_inspection__c",
            "Install_was_ready_for_final_inspection__c",
            "Upgrade_was_ready_for_final_inspection__c",
            "Did_you_have_all_equipment_necessary__c",
            "Resolution__c",
            "Root_Cause__c",
            "Job_complete_picklist__c",
            "Is_the_customer_fully_aware_of_situation__c",
            "Opti_map_picture_uploaded__c",
            "Wall_conduit_painted__c",
            "Roof_conduit_painted__c",
            "Structure_Levels__c",
            "Ceiling_Type__c",
            "Roof_Tilt__c",
            "Roof_Condition__c",
            "Utility_Feed__c",
            "Main_Breaker_Rating__c",
            "Grounding__c",
            "Any_panels_missing_dead_front__c",
            "Siding_Type__c",
            "Any_unpermitted_structures__c",
            "Manufactured_home__c",
            "Took_pictures_of_HUD_Label_and_Decal__c",
            "Roof_Type__c",
            "Needs_follow_up__c",
            "Why_doesn_t_the_work_match_the_plans__c",
            "Why_wasn_t_CU_told_how_to_turn_on_system__c",
            "Why_wasn_t_the_wall_conduit_painted__c",
            "Reason_for_not_completing_roof_work__c",
            "What_needs_to_be_completed_for_tie_in__c",
            "Reason_for_not_completing_tie_in__c",
            "Why_wasn_t_system_paired_no_picture__c",
            "Why_didn_t_CU_approve_layout__c",
            "Why_wasn_t_monitoring_set_up__c",
            "Battery_install_completed__c",
            "Drone_Survey__c",
            "Internet_Available__c",
            "Roof_Lead_Notes__c",
            "Customer_knows_the_status_of_the_job__c",
            "Why_customer_doesn_t_know_status_of_job__c",
            "Did_you_have_enough_tiles_to_fix_broken__c",
            "All_Roof_photos_uploaded_cb__c",
            "Final_walkthrough_completed_cb__c",
            "What_roof_work_needs_to_be_completed_C__c",
            "Reason_for_not_completing_roof_work_C__c",
            "What_type_of_tech_needs_to_be_sent_out__c",
            "Why_not_supplied_with_all_equipment__c",
            "How_many_tiles_are_still_needed__c",
            "Does_the_equipment_installed_match_the_p__c",
            "MPU_Complete__c",
            "Has_power_been_restored_to_the_home__c",
            "AHJ_inspection_outcome__c",
            "Has_the_solar_been_tied_in__c",
            "Any_damage_to_siding_stucco__c",
            "Monitoring_set_up__c",
            "Commissioning_Screen_Shots__c",
            "All_electrical_photos_uploaded__c",
            "Solar_Electrician_Notes__c",
            "CAD_Errors__c",
            "Reason_for_not_completing_MPU__c",
            "Why_did_we_fail_the_AHJ_inspection__c",
            "Why_was_power_not_restored_to_the_home__c",
            "Why_has_the_solar_not_been_tied_in__c",
            "Why_hasn_t_the_battery_been_installed__c",
            "Able_to_fix_on_site__c",
            "Roof_mossy_or_slippery__c",
            "Fragile_or_lightweight_tile__c",
            "Anything_else_that_needs_to_be_done__c",
            "Estimated_time_needed_to_resolve_Service__c",
            "Was_the_customer_home__c",
            "Are_they_aware_of_the_situation__c",
            "Any_other_notes_comments_from_the_cu__c",
            "Did_the_Inverter_Optimizer_need_to_be_R__c",
            "Service_Tech_Notes__c",
            "Case_RMA_number__c",
            "Old_Serial__c",
            "New_Serial__c",
            "All_panels_paired__c",
            "S_ok__c",
            "Was_the_job_ready_for_inspection__c",
            "What_work_was_remaining__c",
            "Were_you_able_to_correct_those_items__c",
            "Job_card_uploaded_cb__c",
            "Inspection_Tech_Notes__c",
            "Visible_roof_damage__c",
            "Rain_gutters_clean__c",
            "Workmanship_issues__c",
            "Trash_left_at_the_job_site__c",
            "Clean_electrical_work__c",
            "Does_system_work_Does_it_have_S_OK__c",
            "Panels_level__c",
            "Installers_leave_work_incomplete__c",
            "Tile_replacement_flashings_secured__c",
            "Does_AHJ_require_lathe_inspection__c",
            "Estimated_time_needed_to_finish_Roof_Ins__c",
            "Solar_electrical_work_complete__c",
            "What_electrical_work_needs_to_be_comp__c",
            "Reason_for_not_completing_SE_work__c",
            "Est_time_needed_to_finish_SE_install__c",
            "Ready_for_final_inspection_E__c",
            "Did_you_have_all_equipment_necessary_E__c",
            "Customer_knows_the_status_of_the_job_E__c",
            "Final_Solar_Elec_walkthrough_completed__c",
            "Schedulable__r.Electrical_Type__c",
            "What_type_of_battery__c",
            "Tested_battery_backup_for_10_minutes__c",
            "Verify_CT_s_are_correct__c",
            "Customer_confirmed_circuits__c",
            "All_battery_photos_uploaded__c",
            "Schedulable__r.Derate__c",
            "Has_the_Derate_been_completed__c",
            "Why_was_the_derate_not_completed__c",
            "Type_of_subpanel_work__c",
            "Schedulable__r.New_Subpanel__c",
            "Schedulable__r.Subpanel_Upgrade__c",
            "Was_the_electrical_work_completed__c",
            "Schedulable__r.Transfer_Switch__c",
            "Type_of_Transfer_Switch__c",
            "Was_the_transfer_switch_completed__c",
            "Electrician_Notes__c",
            "Was_Derate_Needed__c",
            "Why_wasn_t_the_subpanel_completed__c",
            "Was_the_subpanel_completed__c",
            "Gateway_SN__c"
        ],
        "where": {
            "Id": this.fieldJobId
        }
    };

    // ---------- state: ----------
    state = {
        isEditing: true,
        data: {},
        jobType: '',

        shouldShowAlert: false,
        alertMessage: 'Required fields must be saved before ending job.',

        // fields:
        existingSolarFields: [],
        editFields: [],         // form fields (both displayed fields and editable fields),
        installedFields: [],
        outcomeField: [], // TODO: rename to plural: `outcomeFields`
        roofFields: [],
        cancelledFields: [],
        roofAndGmFields: [],
        surveyCompletionFields: [],
        electricianOptions: [],
        panelsOptions: [],

        multipickFields: [],
        fieldTeam: [],
        fieldTeamPicklist: [],
        activeFieldPersonnel: [],
        assignedCrew: {},
        addidtionalTech: ''
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        window.scrollTo(0, 0);
        axios.all([
            (
                axios.post(
                    '/sf/fieldjob/findNo',
                    this.postRequestFields,
                    {
                        cancelToken: this.source.token
                    })
            ),
            (
                axios.get(
                    '/sf/metaData',
                    {
                        cancelToken: this.source.token
                    })
            )])
            .then(axios.spread((resFieldJob, resMetaData) => {
                const data = _.cloneDeep(resFieldJob.data.records[0]);
                const jobType = data.RecordType.Name;
                const metaDataJobFields = resMetaData.data.jobFields.visible;

                // since schedulable id, `metaDataJobFields`, and `initialData` don't affect rendering, we can avoid setting state:
                this.schedulableId = data['Schedulable__c'];
                this.metaDataJobFields = metaDataJobFields;
                this.initialData = _.cloneDeep(data);

                switch (jobType) {
                    case 'Site Survey':
                        axios.get(
                            '/sf/fieldjob/describeField/Roof_Work_Complete__c;Ceiling_Type__c;Roof_Type__c;Roof_Condition__c;' +
                            'Utility_Feed__c;Main_Breaker_Rating__c;Grounding__c;Siding_Type__c;Waste_Management_Method__c;Select_materials_in_the_trench_path__c;' +
                            'Site_Survey_Outcome__c;What_Was_Partially_Completed__c;What_Was_The_Reason_For_Cancelling__c;Internet_Available__c;' +
                            'Roof_mossy_or_slippery__c;Fragile_or_lightweight_tile__c',
                            {
                                cancelToken: this.source.token
                            })
                            .then((resPickFields) => {
                                const mappedFields = mapJobTypeFields({
                                    data,
                                    jobType,
                                    metaDataJobFields,
                                    pickFields: resPickFields.data
                                });

                                this.pickFields = resPickFields.data;

                                this.setState({
                                    jobType,
                                    editFields: mappedFields.editFields,
                                    surveyCompletionFields: mappedFields.surveyCompletionFields,
                                    existingSolarFields: mappedFields.existingSolarFields,
                                    data,
                                });
                            });
                        break;
                    case 'Inspection':
                        axios.get(
                            '/sf/fieldjob/describeField/Inspection_Outcome__c;What_Was_The_Reason_For_Cancelling__c;What_work_was_remaining__c',
                            {
                                cancelToken: this.source.token
                            })
                            .then((resPickFields) => {
                                const mappedFields = mapJobTypeFields({
                                    data,
                                    jobType,
                                    metaDataJobFields,
                                    pickFields: resPickFields.data
                                });

                                this.pickFields = resPickFields.data;

                                this.setState({
                                    jobType,
                                    editFields: mappedFields.editFields,
                                    pickFields: resPickFields.data,
                                    data,
                                });
                            });
                        break;
                    case 'Install':
                        axios.all([
                            (
                                axios.get(
                                    '/sf/fieldjob/describeField/What_needs_to_be_completed_for_tie_in__c;Reason_for_not_completing_tie_in__c;' +
                                    'Why_doesn_t_the_work_match_the_plans__c;Why_wasn_t_CU_told_how_to_turn_on_system__c;Why_wasn_t_the_wall_conduit_painted__c;' +
                                    'Why_wasn_t_system_paired_no_picture__c;Why_didn_t_CU_approve_layout__c;Why_wasn_t_monitoring_set_up__c;Install_Outcome__c;' +
                                    'Why_customer_doesn_t_know_status_of_job__c;Type_of_subpanel_work__c;What_Was_The_Reason_For_Cancelling__c;What_roof_work_needs_to_be_completed_C__c;' +
                                    'Reason_for_not_completing_roof_work_C__c;What_type_of_tech_needs_to_be_sent_out__c;Why_not_supplied_with_all_equipment__c;' +
                                    'Did_you_have_enough_tiles_to_fix_broken__c;Estimated_time_needed_to_finish_Roof_Ins__c;CAD_Errors__c;' + 
                                    'What_electrical_work_needs_to_be_comp__c;Reason_for_not_completing_SE_work__c;Est_time_needed_to_finish_SE_install__c;' +
                                    'Final_Solar_Elec_walkthrough_completed__c;' +
                                    'Why_was_power_not_restored_to_the_home__c;Gateway_SN__c',
                                    {
                                        cancelToken: this.source.token
                                    })
                            ),
                            (
                                axios.post(
                                    '/sf/contact/findElec',
                                    this.postRequestFindElectricians,
                                    {
                                        cancelToken: this.source.token
                                    })
                            ),
                            (
                                axios.post(
                                    '/sf/equipment/findNo',
                                    this.postRequestFindEquipment,
                                    {
                                        cancelToken: this.source.token
                                    })
                            )])
                            .then(axios.spread((resFollowUp, resElectricians, resEquipment) => {
                                const mappedFields = mapJobTypeFields({
                                    data,
                                    jobType,
                                    metaDataJobFields,
                                    followUpFields: resFollowUp.data,
                                    electriciansFields: resElectricians.data.records,
                                    equipmentFields: resEquipment.data.records
                                });

                                this.setState({
                                    jobType,
                                    editFields: mappedFields.editFields,
                                    followUpFields: resFollowUp.data,
                                    panelsOptions: mappedFields.panelsOptions,
                                    electricianOptions: mappedFields.electricianOptions,
                                    multipickFields: mappedFields.multipickFields,
                                    data,
                                })

                                this.getFieldTeam();
                            }));
                        break;
                    case 'Electrical':
                        axios.get(
                            '/sf/fieldjob/describeField/CAD_Errors__c;Reason_for_not_completing_MPU__c;Why_did_we_fail_the_AHJ_inspection__c;Why_was_power_not_restored_to_the_home__c;' +
                            'Why_has_the_solar_not_been_tied_in__c;Why_hasn_t_the_battery_been_installed__c;Why_not_supplied_with_all_equipment__c;Able_to_fix_on_site__c;' +
                            'Why_customer_doesn_t_know_status_of_job__c;Does_AHJ_require_lathe_inspection__c;What_type_of_battery__c;AHJ_inspection_outcome__c;' +
                            'Why_was_the_derate_not_completed__c;Type_of_subpanel_work__c;Type_of_Transfer_Switch__c;Gateway_SN__c;Reason_for_not_completing_roof_work_C__c;' +
                            'What_type_of_tech_needs_to_be_sent_out__c;Estimated_time_needed_to_resolve_Service__c',
                            {
                                cancelToken: this.source.token
                            })
                            .then((resFollowUp) => {
                                const mappedFields = mapJobTypeFields({
                                    data,
                                    jobType,
                                    metaDataJobFields,
                                    followUpFields: resFollowUp.data
                                });

                                this.setState({
                                    jobType,
                                    editFields: mappedFields.editFields,
                                    installedFields: mappedFields.installedFields,
                                    followUpFields: resFollowUp.data,
                                    data,
                                });

                                this.getFieldTeam();
                            });
                        break;
                    case 'Service Call':
                        axios.get(
                            '/sf/fieldjob/describeField/What_type_of_tech_needs_to_be_sent_out__c;Estimated_time_needed_to_resolve_Service__c',
                            {
                                cancelToken: this.source.token
                            })
                            .then((resFollowUp) => {
                                const mappedFields = mapJobTypeFields({
                                    data,
                                    jobType,
                                    metaDataJobFields,
                                    followUpFields: resFollowUp.data
                                });

                                this.setState({
                                    jobType,
                                    editFields: mappedFields.editFields,
                                    installedFields: mappedFields.installedFields,
                                    followUpFields: resFollowUp.data,
                                    data,
                                });
                            });
                        break;
                    default:
                        // n.b. added block scope since we're in a switch:
                        {
                            const mappedFields = mapJobTypeFields({
                                data,
                                jobType,
                                metaDataJobFields
                            });

                            this.setState({
                                jobType,
                                editFields: mappedFields.editFields,
                                multipickFields: mappedFields.multipickFields,
                                data,
                            });

                            break;
                        }
                }
            }))
            .catch(err => console.log('Axios Error:\n', err));
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    getFieldTeam = () => {
        axios.get(
            '/sf/contact/findOne/' + this.state.data.Schedulable__r.Assigned_Crew__c,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                let contactData = res.data;

                this.setState({assignedCrew: contactData});

                this.getActiveField();
            })
            .catch(err => console.log('(GET) Error For Field Contact: ', err));
    };

    getActiveField = () => {
        let activeFieldPayload = {
            limit: 500,
            fields: {
                Id: 1,
                Name: 1,
                Payroll_ID__c: 1,
                Field_Team__c: 1
            },
            where: {
                Active__c: true,
                "$or": [
                    {
                        "Job_Department__c": "Installation"
                    },
                    {
                        "Job_Department__c": "Electrician"
                    }
                ]
            },
            orderBy: {
                Name: 1
            }
        };

        axios.post(
            '/sf/contact/findNo',
            activeFieldPayload,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const data = res.data;
                const field = [];

                _.each(data.records, function(v){
                    field.push({value: v.Name, id: v.Payroll_ID__c});
                });

                const filterField = _.uniqBy(field, function(o) { return o.value; });
                const orderedField = _.orderBy(filterField, ['value']);

                this.setState({
                    activeFieldPersonnel: orderedField,
                    fieldTeamPicklist: _.filter(data.records, {'Field_Team__c': this.state.assignedCrew.Field_Team__c})
                });
            })
            .catch(err => console.log('(GET) Error tyring to fetch active field personnel: ', err));
    };

    // ----------  methods: ----------
    /**
     * @method
     * @memberOf FieldJob
     *
     * Creates the payload for when user selects to complete/end a job.
     *
     * N.b. the logic and data processed to "complete"/"end" the job is different than for just "saving" the fields.
     * @return {object}: payload object with the requisite object properties for the API call -- used in `completeJob()`
     */
    prepRecordsForJobCompletion = () => {
        const data = _.cloneDeep(this.state.data);
        const dateComplete = moment().format(this.SF_DATE_TIME_FORMAT);
        const scDateComplete = moment().format(this.SC_DATE_FORMAT);

        data.Clocked_End__c = dateComplete;
        data.Completed_By__c = this.props.user.apiId;
        data.Status__c = "Complete";

        const payloadData = {
            fieldJob: {
                Id:                 data.Id,
                Clocked_End__c:     data.Clocked_End__c,
                Completed_By__c:    data.Completed_By__c,
                Status__c:          data.Status__c,
            },
            session: {}, // n.b. "session" is also known as "schedulable" or "scheduled"  throughout the rest of the code
            opportunity: {}
        };

        // when a job is completed it needs to update its related records:
        const extraCompletion = {
            "Site Survey": {
                Schedulable__r: {
                    Site_Survey_Completed__c: scDateComplete,
                    // Survey_Completed_By__c: data.Completed_By__c
                }
            },
            Install: {
                Schedulable__r: {
                    Schedulable_Complete__c: scDateComplete,
                    // Assigned_Crew__c: data.Completed_By__c
                }
            },
            Inspection: {
                Schedulable__r: {
                    Schedulable_Complete__c: scDateComplete,
                    // Assigned_Crew__c: data.Completed_By__c
                }
            },
            "Service Call": {
                Schedulable__r: {
                    Schedulable_Complete__c: scDateComplete,
                    // Assigned_Crew__c: data.Completed_By__c
                }
            },
            Electrical: {
                Schedulable__r: {
                    Schedulable_Complete__c: scDateComplete,
                    // Assigned_Crew__c: data.Completed_By__c
                }
            }
        };

        // this section of code is copied from version 1 of the dashboard app:
        // we create a variable to prevent potential invalid reference of `this` keyword inside `forEach` loops:
        const jobType = this.state.jobType;
        if (_.has(extraCompletion, jobType)) {
            _.forEach(extraCompletion[jobType], function (val, key) {
                switch (key) {
                    case "Schedulable__r":
                        _.forEach(val, function (value, fieldKey) {
                            payloadData.session[fieldKey] = value;
                        });
                        break;
                    case "Opportunity":
                        _.forEach(val, function (value, fieldKey) {
                            payloadData.opportunity[fieldKey] = value;
                        });
                        break;
                    case "Field_Job__r":
                        _.forEach(val, function (value, fieldKey) {
                            payloadData.fieldJob[fieldKey] = value;
                        });
                        break;
                    default:
                        break;
                }
            });
        }

        return payloadData;
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Marks the job as completed and updates records (opportunity and schedulable).
     *
     * The majority of the method involves processing data and updating related records.
     */
    completeJob = () => {
        // check if required fields are filled out -- if not, alert user:
        if (!this.formIsValid()) {
            // show error alert:
            this.setState({
                shouldShowAlert: true,
                alertMessage: 'Required fields must be saved before ending job.'
            });
            setTimeout(() => {
                // condition to guard against setting state when component is unmounted:
                this.setState({ shouldShowAlert: false });
            }, 5500);

            // since form is invalid, return early:
            return;
        }

        // process payload data:
        const payloadData = this.prepRecordsForJobCompletion();

        // update records:
        // TODO: should add alert message on API fail (in `.catch()`):
        const updateSession = () => {
            if (!_.isEmpty(payloadData.session)) {
                payloadData.session.Id = this.state.data.Schedulable__c;

                return (
                    axios.put(
                        '/sf/schedulable/update',
                        payloadData.session,
                        {
                            cancelToken: this.source.token
                        })
                        .catch(err => console.log('PUT Error:\n', err))
                );
            }
        };

        const updateOpportunity = () => {
            if (!_.isEmpty(payloadData.opportunity)) {
                payloadData.opportunity.Id = this.state.data.Opportunity_ID__c;

                return (
                    axios.put(
                        '/sf/opportunity/update',
                        payloadData.opportunity,
                        {
                            cancelToken: this.source.token
                        })
                        .catch(err => console.log('PUT Error:\n', err))
                );
            }
        };

        // n.b. this promise is always called for ending a job:
        const updateFieldJob = () => {
            return (
                axios.put(
                    '/sf/fieldjob/update',
                    payloadData.fieldJob,
                    {
                        cancelToken: this.source.token
                    })
                    .catch(err => console.log('PUT Error:\n', err))
            );
        };

        // update records and then fetch updated data:
        // send multiple concurrent requests with `axios.all` <https://github.com/axios/axios#example>:
        axios.all([
                updateFieldJob(),
                updateSession(),
                updateOpportunity()
            ])
            // after all requests complete, then refresh page:
            .then(axios.spread(() => {

                this.setState({
                    shouldShowAlert: true,
                    alertMessage: 'Job successfully completed.'
                });
                setTimeout(() => {
                    // condition to guard against setting state when component is unmounted:
                    this.setState({ shouldShowAlert: false });
                }, 5500);

                //window.location.reload();
            }));
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for <input> tags -- updates state.
     * Each "field" is an object in `this.state.editFields` (`editFields` is initial data that is mapped via `mapJobTypeFields.js`).
     *
     * @param fieldName {string}: Name of field -- these are the names of fields from `editFields`/`mapJobTypeFields`.
     * @param value {string}: Value of updated input.
     */
    fieldOnChange = (fieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field object's position in the array:
        const fieldIndex = editFields.findIndex(field => field.name === fieldName);

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].value = value;
        }

        this.setState({ editFields });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for <input> tags, specifically for the 'Install_Outcome__c' field -- updates state.
     *
     * @param fieldName {string}: Name of field -- this is always `Install_Outcome__c` but is being kept to be consistent with
     * other similar handlers.
     * @param value {string}: Value of updated input.
     */
    installFieldOnChange = (fieldName, value) => {
        const data = _.cloneDeep(this.state.data);

        data[fieldName] = value;
        const mappedFields = mapJobTypeFields({
            data,
            jobType: this.state.jobType,
            metaDataJobFields: this.metaDataJobFields,
            followUpFields: this.state.followUpFields,
            electriciansFields: this.electriciansFields,
            equipmentFields: this.equipmentFields
        });

        this.setState({
            editFields: mappedFields.editFields,
            followUpFields: mappedFields.followUpFields,
            surveyCompletionFields: mappedFields.surveyCompletionFields,
            existingSolarFields: mappedFields.existingSolarFields,
            multipickFields: mappedFields.multipickFields,
            data
        });
    };

        /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for <input> tags, specifically for the 'Inspection_Outcome__c' field -- updates state.
     *
     * @param fieldName {string}: Name of field -- this is always `Inspection_Outcome__c` but is being kept to be consistent with
     * other similar handlers.
     * @param value {string}: Value of updated input.
     */
    inspectionFieldOnChange = (fieldName, value) => {
        const data = _.cloneDeep(this.state.data);

        data[fieldName] = value;
        const mappedFields = mapJobTypeFields({
            data,
            jobType: this.state.jobType,
            metaDataJobFields: this.metaDataJobFields,
            pickFields: this.pickFields
        });

        this.setState({
            editFields: mappedFields.editFields,
            pickFields: this.pickFields,
            data,
        });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for <input> tags, specifically for the 'Site_Survey_Outcome__c' field -- updates state.
     *
     * @param fieldName {string}: Name of field -- this is always `Site_Survey_Outcome__c` but is being kept to be consistent with
     * other similar handlers.
     * @param value {string}: Value of updated input.
     */
    siteSurveyFieldOnChange = (fieldName, value) => {
        const data = _.cloneDeep(this.state.data);

        data[fieldName] = value;
        const mappedFields = mapJobTypeFields({
            data,
            jobType: this.state.jobType,
            metaDataJobFields: this.metaDataJobFields,
            pickFields: this.pickFields
        });

        this.setState({
            editFields: mappedFields.editFields,
            surveyCompletionFields: mappedFields.surveyCompletionFields,
            existingSolarFields: mappedFields.existingSolarFields,
            data
        });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for subfields -- updates state.
     * Note that each "field" is an object in `this.state.editFields` (`editFields` is initial data that is mapped via `mapJobTypeFields.js`).
     * (As seen in `mapJobTypeFields`), fields can optionally have a`subfield` property (also an object).
     * This method updates the `subfield` property object.
     *
     * @param fieldName {string}: Name of field. Even though this method updates the `subfield` of the object, we look up the `fieldName`.
     * @param subFieldName {string}: Name of the subfield property.
     * @param value {string}: Value of updated input.
     */
    subFieldOnChange = (fieldName, subFieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.subField && field.subField.name === subFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].subField.value = value;
        }

        this.setState({ editFields });
    };


    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for subfields -- updates state.
     * Note that each "field" is an object in `this.state.editFields` (`editFields` is initial data that is mapped via `mapJobTypeFields.js`).
     * (As seen in `mapJobTypeFields`), fields can optionally have `subfield` and `secondSubField` properties (also objects).
     * This method updates the `secondSubFieldName` property object.
     *
     * @param fieldName {string}: Name of field. Even though this method updates the `subfield` of the object, we look up the `fieldName`.
     * @param secondSubFieldName {string}: Name of the secondary subfield property.
     * @param value {string}: Value of updated input.
     */
    secondarySubFieldOnChange = (fieldName, secondSubFieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.secondSubField && field.secondSubField.name === secondSubFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].secondSubField.value = value;
        }

        this.setState({ editFields });
    };

    thirdSubFieldOnChange = (fieldName, thirdSubFieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.thirdSubField && field.thirdSubField.name === thirdSubFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].thirdSubField.value = value;
        }

        this.setState({ editFields });
    };

    thirdSubFieldOnChangeMultiPick = (fieldName, thirdSubFieldName, value) => {
        const values = _.slice(value, [0], [value.length])
        const groupedValues = _.reduce(values, function(pickList, n) {
            return pickList + n.value + ';';
          }, '');

        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.thirdSubField && field.thirdSubField.name === thirdSubFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].thirdSubField.value = groupedValues;
        }

        this.setState({ editFields });
    };

    fourthSubFieldOnChange = (fieldName, fourthSubFieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.fourthSubField && field.fourthSubField.name === fourthSubFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].fourthSubField.value = value;
        }

        this.setState({ editFields });
    };

    fifthSubFieldOnChange = (fieldName, fifthSubFieldName, value) => {
        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.fifthSubField && field.fifthSubField.name === fifthSubFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].fifthSubField.value = value;
        }

        this.setState({ editFields });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for when field is updated, specifically for the list items in 'Site_Survey_Outcome__c' -- updates state.
     *
     * @param fieldName {string}: Name of field in the `surveyCompletionFields` list.
     * @param value {string}: Value of updated input.
     */
    surveyCompletionFieldsOnChange = (fieldName, value) => {
        const data = _.cloneDeep(this.state.data);

        // because changing the completion field changes the displayed fields -- we have to recall `mapJobTypeFields`:
        data[fieldName] = value;
        const mappedFields = mapJobTypeFields({
            data,
            jobType: this.state.jobType,
            metaDataJobFields: this.metaDataJobFields,
            pickFields: this.pickFields
        });

        this.setState({
            editFields: mappedFields.editFields,
            surveyCompletionFields: mappedFields.surveyCompletionFields,
            data
        });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for when field is updated, specifically for the list items in 'Install_Outcome__c' -- updates state.
     *
     * @param fieldName {string}: Name of field in the `installedFields` list.
     * @param value {string}: Value of updated input.
     */
    installedFieldOnChange = (fieldName, value) => {
        const installedFields = _.cloneDeep(this.state.installedFields);

        // find the field object's position in the array:
        const fieldIndex = installedFields.findIndex(field => field.name === fieldName);

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            installedFields[fieldIndex].value = value;
        }

        this.setState({ installedFields });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Handler for when field is updated, specifically for the list items in 'Is_There_Existing_Solar__c' -- updates state.
     *
     * @param fieldName {string}: Name of field in the `existingSolarFields` list.
     * @param value {string}: Value of updated input.
     */
    existingSolarFieldOnChange = (fieldName, value) => {
        const existingSolarFields = _.cloneDeep(this.state.existingSolarFields);

        // find the field object's position in the array:
        const fieldIndex = existingSolarFields.findIndex(field => field.name === fieldName);

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            existingSolarFields[fieldIndex].value = value;
        }

        this.setState({ existingSolarFields });
    };

    multipickFieldOnChange = (fieldName, subFieldName, value) => {
        const values = _.slice(value, [0], [value.length])
        const groupedValues = _.reduce(values, function(pickList, n) {
            return pickList + n.value + ';';
          }, '');

        const editFields = _.cloneDeep(this.state.editFields);

        // find the field/subfield object's position in the array:
        const fieldIndex = editFields.findIndex(field => {
            return field.name === fieldName && field.subField && field.subField.name === subFieldName;
        });

        // if we find the object, update the value:
        if (fieldIndex !== -1) {
            editFields[fieldIndex].subField.value = groupedValues;
        }

        this.setState({ editFields });
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Checks if required fields are filled out.
     * Used for both saving and completing the job.
     *
     * @return {boolean}
     */
    formIsValid = () => {
        for (let field of this.state.editFields) {
            // Did_the_customer_sign_the_NEM_form__c:
            if (field.name === 'Did_the_customer_sign_the_NEM_form__c' &&
                !field.value &&
                !this.state.data['Schedulable__r']['NEM_Signed_Date__c']
            ) {
                return false;
            }

            // general fields:
            if (field.required && !field.value && field.type !== 'checkbox' && field.name !== 'Did_the_customer_sign_the_NEM_form__c'){
                return false;
            }

            // check 'completion' fields:
            if (this.state.jobType === 'Site Survey' &&
                field.name === 'Site_Survey_Outcome__c' &&
                field.value) {

                // only one of the elements in the list needs to have a truthy value:
                let issurveyCompletionFieldValid = false;
                for (let surveyCompletionField of this.state.surveyCompletionFields) {
                    if (surveyCompletionField.required && surveyCompletionField.value) {
                        issurveyCompletionFieldValid = true;
                    }
                }

                if (!issurveyCompletionFieldValid) {
                    return  false;
                }
            }

            // check 'existing solar' fields:
            if (this.state.jobType === 'Site Survey' &&
                field.name === 'Is_There_Existing_Solar__c' &&
                field.value === 'Yes') {
                for (let existingSolarField of this.state.existingSolarFields) {
                    if (existingSolarField.required && !existingSolarField.value) {
                        return false;
                    }
                }
            }
        }

        return  true;
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Saves updated fields -- submits API (PUT) requests
     * N.b. "saving" is different than "completing" a job.
     */
    saveFields = () => {
        /* SF-1085 Allowing to save EOJ when required fields aren't enetered
        // check if required fields are filled out -- if not, alert user:
        if (!this.formIsValid()) {
            // show error alert:
            this.setState({
                shouldShowAlert: true,
                alertMessage: 'Please fill out required fields.'
            });
            setTimeout(() => {
                // condition to guard against setting state when component is unmounted:
                this.setState({ shouldShowAlert: false });
            }, 5500);

            // since form is invalid, return early:
            return;
        }
        */

        const {
            updatedFieldJob,
            updatedScheduled,
            updatedOpportunity
        } = this.processDataForSave();

        const updateFieldJob = () => {
            return (
                axios.put(
                    '/sf/fieldjob/update',
                    {
                        Id: this.fieldJobId,
                        ...updatedFieldJob
                    },
                    {
                        cancelToken: this.source.token
                    })
            );
        };

        const updateSchedulable = () => {
            if (!_.isEmpty(updatedScheduled)) {
                return (
                    axios.put(
                        '/sf/schedulable/update',
                        {
                            Id: this.schedulableId,
                            ...updatedScheduled
                        },
                        {
                            cancelToken: this.source.token
                        })
                );
            }
        };

        const updateOpportunity = () => {
            if(!_.isEmpty(updatedOpportunity)) {
                return (
                    axios.put(
                        '/sf/opportunity/update',
                        {
                            Id: this.initialData.Opportunity_ID__c,
                            ...updatedOpportunity
                        },
                        {
                            cancelToken: this.source.token
                        })
                );
            }
        };

        // send multiple concurrent requests with `axios.all` <https://github.com/axios/axios#example>:
        axios.all([
                updateFieldJob(),
                updateSchedulable(),
                updateOpportunity()
            ])
            // after all requests complete, change to display mode:
            .then(axios.spread(() => {
                this.setState({ isEditing: false });
            }))
            .catch(err => console.log('PUT error: \n', err));
    };

    /**
     * @method
     * @memberOf FieldJob
     *
     * Creates the request payloads for saving the form fields on the page.
     * When the form is saved, we potentially make up to 3 different calls: update fieldJobs, update opportunity, and update scheduled (session).
     * This method looks to see which fields have changed from last save (i.e. page load) and we only pass those fields/values as the response object.
     *
     *
     * @return {{updatedFieldJob: {}, updatedScheduled: {}, updatedOpportunity: {}}}
     */
    processDataForSave = () => {
        const updatedFieldJob = {};
        const updatedScheduled = {};
        const updatedOpportunity = {};

        for(let field of this.state.editFields) {
            if(this.state.fieldTeam.length !== 0){
                let values = this.state.fieldTeam;
                let installCrew = '';
                values.forEach(value => {
                    installCrew = installCrew + value + ',';
                });

                if(this.state.addidtionalTech !== ''){
                    installCrew = installCrew + this.state.addidtionalTech;
                }

                updatedFieldJob.Install_Crew__c = installCrew;
            }

            // if the edited field changed from the original fetched data, we want to update the field in PUT request:
            if (this.initialData[field.name] !== field.value) {
                updatedFieldJob[field.name] = field.value;
            }

            // if subfield is different from original data, we'll update field:
            if (field.subField) {
                if (this.initialData[field.subField.name] !== field.subField.value) {
                    updatedFieldJob[field.subField.name] = field.subField.value;
                }
            }

            // if secondSubField is different from original data, we'll update field:
            if (field.secondSubField) {
                if (this.initialData[field.secondSubField.name] !== field.secondSubField.value) {
                    updatedFieldJob[field.secondSubField.name] = field.secondSubField.value;
                }
            }

            // if thirdSubField is different from original data, we'll update field:
            if (field.thirdSubField) {
                if (this.initialData[field.thirdSubField.name] !== field.thirdSubField.value) {
                    updatedFieldJob[field.thirdSubField.name] = field.thirdSubField.value;
                }
            }

            // if fourthSubField is different from original data, we'll update field:
            if (field.fourthSubField) {
                if (this.initialData[field.fourthSubField.name] !== field.fourthSubField.value) {
                    updatedFieldJob[field.fourthSubField.name] = field.fourthSubField.value;
                }
            }

            // if fifthSubField is different from original data, we'll update field:
            if (field.fifthSubField) {
                if (this.initialData[field.fifthSubField.name] !== field.fifthSubField.value) {
                    updatedFieldJob[field.fifthSubField.name] = field.fifthSubField.value;
                }
            }

            switch (this.state.jobType) {
                case 'Electrical':
                    break;
                case 'Site Survey':
                    // update survey completion fields:
                    if (field.name === 'Site_Survey_Outcome__c' &&
                        field.value) {
                        for (let surveyCompletionField of this.state.surveyCompletionFields) {
                            if (this.initialData[surveyCompletionField.name] !== surveyCompletionField.value) {
                                updatedFieldJob[surveyCompletionField.name] = surveyCompletionField.value;
                            }
                        }
                    }

                    // update existing solar fields:
                    if (field.name === 'Is_There_Existing_Solar__c' &&
                        field.value === 'Yes') {
                        for (let existingSolarField of this.state.existingSolarFields) {
                            if (this.initialData[existingSolarField.name] !== existingSolarField.value) {
                                updatedFieldJob[existingSolarField.name] = existingSolarField.value;
                            }
                        }
                    }

                    // update opportunity - Dropbox link:
                    if (field.name === 'Dropbox_Link__c' && field.value) {
                        updatedOpportunity['SS_Dropbox_Link__c'] = field.value;
                    }
                    break;
                case 'Install':
                    // if we detect that the value has been updated from original data:
                    if ((this.initialData[field.name] !== field.value) &&
                        field.value)
                    {

                        // update opportunity:
                        // n.b. the differences in property names is NOT a mistake:
                        if (field.name === 'Tie_in_completed__c' &&
                            field.value === 'Yes') {
                            updatedOpportunity['Tie_In_Complete__c'] = moment().format(this.SF_DATE_FORMAT);
                        }
                    }
                    break;
                case 'Inspection':
                    // if we detect that the value has been updated from original data:
                    if ((this.initialData[field.name] !== field.value) &&
                        field.value)
                    {

                        // add opportunity timestamp when inspection fails:
                        // if (field.name === 'Inspection_Outcome__c' &&
                        //     field.value === 'Fail') {
                        //     updatedOpportunity['Inspection_Failed__c'] = moment().format(this.SF_DATE_FORMAT);
                        // }

                        // add opportunity timestamp when inspection passes:
                        // if (field.name === 'Inspection_Outcome__c' &&
                        //     field.value === 'Pass') {
                        //     updatedOpportunity['Inspection_Passed__c'] = moment().format(this.SF_DATE_FORMAT);
                        // }
                    }
                    break;
                case 'Service Call':
                    if ((this.initialData[field.name] !== field.value) &&
                        field.value)
                    {
                        if (field.name === 'Root_Cause__c' && field.value) {
                            updatedScheduled['Root_Cause__c'] = field.value;
                        }

                        if (field.name === 'Resolution__c' && field.value) {
                            updatedScheduled['Resolution__c'] = field.value;
                        }
                    }
                    break;
                default:
                    break;
            }
        }

        // update scheduled notes if necessary -- used in all job types:
        if (updatedFieldJob['Job_Notes__c']) {
            updatedScheduled['Field_Notes__c'] = updatedFieldJob['Job_Notes__c'];
        }

        // add schedulable id:
        if (!_.isEmpty(updatedScheduled)) {
            updatedScheduled.Id = this.initialData.Schedulable__c;
        }

        // add opportunity id:
        if (!_.isEmpty(updatedOpportunity)) {
            updatedOpportunity.Id = this.initialData.Opportunity_ID__c;
        }

        return {
            updatedFieldJob,
            updatedScheduled,
            updatedOpportunity
        };
    };

    updateFieldTeam = (selectedOptions) => {
        if(selectedOptions.length !== 0){
            let values = [];
            Array.from(selectedOptions).forEach(option => {
                values.push(option.value);
            });

            this.setState({fieldTeam: values});
        }
    };

    render() {
        return (
            <div>
                <Alert showAlert={this.state.shouldShowAlert}>{this.state.alertMessage}</Alert>

                {/* ---------- Field List Attributes: ---------- */}
                <div className="row mt-3">
                    {/* ----- row 1: ----- */}
                    <div className="col-12 col-md-6 mb-2">
                        <h4>name</h4>
                        <h1>{ this.state.data.Schedulable__r ? this.state.data.Schedulable__r.Customer_Name__c : '' }</h1>
                    </div>
                    <div className="col-12 col-md-6 mb-2">
                        <h4>type</h4>
                        <h1>{ this.state.data.Type__c }</h1>
                    </div>

                    {/* ----- row 2: ----- */}
                    <div className="col-12 col-md-6 mb-2">
                        <h4>job date</h4>
                        <h1>{ moment(this.state.data.Job_Date__c).format('MMM D, YYYY') }</h1>
                    </div>
                    <div className="col-12 col-md-6 mb-2">
                        <h4>scheduled time</h4>
                        <h1>{ this.state.data.Scheduled_Time_Slot__c }</h1>
                    </div>

                    {/* ----- row 3: ----- */}
                    <div className="col-12 col-md-6 mb-2">
                        <h4>job start</h4>
                        <h1>{ moment(this.state.data.Clocked_Start__c).format('MM/D/YYYY h:mmA') }</h1>
                    </div>
                    <div className="col-12 col-md-6 mb-2">
                        <h4>job end</h4>
                        <h1>{ this.state.data.Clocked_End__c ? moment(this.state.data.Clocked_End__c).format('MM/D/YYYY h:mmA') : '---' }</h1>
                    </div>

                    {/* ----- row 4: ----- */}
                    <div className="col-12">
                        <h4>Follow Up Needed?</h4>
                        <h1>{this.state.data.Follow_up_needed_picklist__c}</h1>
                    </div>
                </div>

                {/* ----- CTA Buttons: ----- */}
                <div className="d-flex justify-content-between mt-5 mb-4">
                    {/* the ternary expression prevents user from clicking the link before `schedulableId` has been fetched: */}
                    <Link to={ this.schedulableId ? `/scheduled/${this.schedulableId}` : ''}>
                        <button className="btn btn-outline" type="button">Back</button>
                    </Link>
                    <div className={this.state.isEditing ? 'text-right' : 'd-none'}>
                        <button className="btn btn-outline" type="button" onClick={ () => this.setState({ isEditing: false }) }>Cancel</button>
                        <button className="btn btn-outline ml-2" type="button" onClick={() => this.saveFields()}>Save</button>
                    </div>
                    <div className={this.state.isEditing ? 'd-none' : 'text-right'}>
                        <a
                            href={formatLink(this.state.data.Schedulable__r ? this.state.data.Schedulable__r.Jobsite_Photos__c : '')}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <button className="btn btn-outline">Upload</button>
                        </a>
                        <button className="btn btn-outline ml-2" type="button" onClick={ () => this.setState({ isEditing: true }) }>Edit</button>
                    </div>
                </div>

                {/* ---------- Form Fields: ---------- */}
                {
                    // ---------- If Edit Mode: ----------
                    this.state.isEditing ?
                        (
                            <>
                                {
                                    // ---------- form fields: ----------
                                    this.state.editFields.map((field, fieldIndex) => {
                                        return (
                                            <div key={fieldIndex}>
                                                {
                                                    field.name === 'Install_Outcome__c' ?
                                                    <div>
                                                        <h1>Roof Install Questions</h1>
                                                        <label className="text-white">Today's Crew Members (Select All):</label>
                                                        <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                            { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                        </select>

                                                        <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                        <Downshift 
                                                            onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                            itemToString={item => (item ? item.value : '')}
                                                        >
                                                            {({
                                                                getInputProps,
                                                                getItemProps,
                                                                getLabelProps,
                                                                getMenuProps,
                                                                isOpen,
                                                                inputValue,
                                                                highlightedIndex,
                                                                selectedItem,
                                                                getToggleButtonProps
                                                            }) => (
                                                            <div>
                                                                <div className="w-72 flex flex-col gap-1">
                                                                    <div className="flex bg-white gap-0.5">
                                                                        <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                    </div>
                                                                </div>
                                                                <ul className="list-group" {...getMenuProps()}>
                                                                    {
                                                                        isOpen ? 
                                                                        this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                            .map((item, index) => (
                                                                            <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                style: {
                                                                                    backgroundColor:
                                                                                    highlightedIndex === index ? 'lightgray' : 'white',
                                                                                    fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                },
                                                                                })}
                                                                            >
                                                                                {item.value}
                                                                            </li>
                                                                        ))
                                                                        : null
                                                                    }
                                                                </ul>
                                                            </div>
                                                            )}
                                                        </Downshift>
                                                    </div>
                                                    :
                                                    ''
                                                }
                                                {
                                                    field.name === 'Does_the_equipment_installed_match_the_p__c' && this.state.data.Type__c !== 'Electrical' ?
                                                    <div>
                                                        <br></br>
                                                        <h1>Solar Electrical Questions</h1>
                                                    </div>
                                                    :
                                                    ''
                                                }
                                                {
                                                    this.state.data.Type__c === 'Electrical' ?
                                                        this.state.data.Schedulable__r.Electrical_Type__c === 'MPU' && field.name === 'Does_the_equipment_installed_match_the_p__c'?

                                                        <div>
                                                            <br></br>
                                                            <h1>Main Panel Upgrade Questions</h1>
                                                            <label className="text-white">Today's Crew Members (Select All):</label>
                                                            <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                                { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                            </select>

                                                            <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                            <Downshift 
                                                                onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                                itemToString={item => (item ? item.value : '')}
                                                            >
                                                                {({
                                                                    getInputProps,
                                                                    getItemProps,
                                                                    getLabelProps,
                                                                    getMenuProps,
                                                                    isOpen,
                                                                    inputValue,
                                                                    highlightedIndex,
                                                                    selectedItem,
                                                                    getToggleButtonProps
                                                                }) => (
                                                                <div>
                                                                    <div className="w-72 flex flex-col gap-1">
                                                                        <div className="flex bg-white gap-0.5">
                                                                            <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                        </div>
                                                                    </div>
                                                                    <ul className="list-group" {...getMenuProps()}>
                                                                        {
                                                                            isOpen ? 
                                                                            this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                                .map((item, index) => (
                                                                                <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                    style: {
                                                                                        backgroundColor:
                                                                                        highlightedIndex === index ? 'lightgray' : 'white',
                                                                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                    },
                                                                                    })}
                                                                                >
                                                                                    {item.value}
                                                                                </li>
                                                                            ))
                                                                            : null
                                                                        }
                                                                    </ul>
                                                                </div>
                                                                )}
                                                            </Downshift>
                                                        </div>
                                                        :

                                                        this.state.data.Schedulable__r.Electrical_Type__c === 'Battery' && field.name === 'What_type_of_battery__c' ?

                                                        <div>
                                                            <br></br>
                                                            <h1>Battery Install Questions</h1>
                                                            <label className="text-white">Today's Crew Members (Select All):</label>
                                                            <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                                { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                            </select>

                                                            <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                            <Downshift 
                                                                onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                                itemToString={item => (item ? item.value : '')}
                                                            >
                                                                {({
                                                                    getInputProps,
                                                                    getItemProps,
                                                                    getLabelProps,
                                                                    getMenuProps,
                                                                    isOpen,
                                                                    inputValue,
                                                                    highlightedIndex,
                                                                    selectedItem,
                                                                    getToggleButtonProps
                                                                }) => (
                                                                <div>
                                                                    <div className="w-72 flex flex-col gap-1">
                                                                        <div className="flex bg-white gap-0.5">
                                                                            <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                        </div>
                                                                    </div>
                                                                    <ul className="list-group" {...getMenuProps()}>
                                                                        {
                                                                            isOpen ? 
                                                                            this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                                .map((item, index) => (
                                                                                <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                    style: {
                                                                                        backgroundColor:
                                                                                        highlightedIndex === index ? 'lightgray' : 'white',
                                                                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                    },
                                                                                    })}
                                                                                >
                                                                                    {item.value}
                                                                                </li>
                                                                            ))
                                                                            : null
                                                                        }
                                                                    </ul>
                                                                </div>
                                                                )}
                                                            </Downshift>
                                                        </div>
                                                        :

                                                        this.state.data.Schedulable__r.Electrical_Type__c === 'Miscellaneous Electrical' && field.name === 'Has_the_Derate_been_completed__c' ?

                                                        <div>
                                                            <br></br>
                                                            <h1>Derate Misc. Questions</h1>
                                                            <label className="text-white">Today's Crew Members (Select All):</label>
                                                            <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                                { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                            </select>

                                                            <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                            <Downshift 
                                                                onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                                itemToString={item => (item ? item.value : '')}
                                                            >
                                                                {({
                                                                    getInputProps,
                                                                    getItemProps,
                                                                    getLabelProps,
                                                                    getMenuProps,
                                                                    isOpen,
                                                                    inputValue,
                                                                    highlightedIndex,
                                                                    selectedItem,
                                                                    getToggleButtonProps
                                                                }) => (
                                                                <div>
                                                                    <div className="w-72 flex flex-col gap-1">
                                                                        <div className="flex bg-white gap-0.5">
                                                                            <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                        </div>
                                                                    </div>
                                                                    <ul className="list-group" {...getMenuProps()}>
                                                                        {
                                                                            isOpen ? 
                                                                            this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                                .map((item, index) => (
                                                                                <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                    style: {
                                                                                        backgroundColor:
                                                                                        highlightedIndex === index ? 'lightgray' : 'white',
                                                                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                    },
                                                                                    })}
                                                                                >
                                                                                    {item.value}
                                                                                </li>
                                                                            ))
                                                                            : null
                                                                        }
                                                                    </ul>
                                                                </div>
                                                                )}
                                                            </Downshift>
                                                        </div>
                                                        :

                                                        this.state.data.Schedulable__r.Electrical_Type__c === 'Miscellaneous Electrical' && field.name === 'Type_of_subpanel_work__c' ?

                                                        <div>
                                                            <br></br>
                                                            <h1>Subpanel Misc. Questions</h1>
                                                            <label className="text-white">Today's Crew Members (Select All):</label>
                                                            <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                                { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                            </select>

                                                            <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                            <Downshift 
                                                                onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                                itemToString={item => (item ? item.value : '')}
                                                            >
                                                                {({
                                                                    getInputProps,
                                                                    getItemProps,
                                                                    getLabelProps,
                                                                    getMenuProps,
                                                                    isOpen,
                                                                    inputValue,
                                                                    highlightedIndex,
                                                                    selectedItem,
                                                                    getToggleButtonProps
                                                                }) => (
                                                                <div>
                                                                    <div className="w-72 flex flex-col gap-1">
                                                                        <div className="flex bg-white gap-0.5">
                                                                            <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                        </div>
                                                                    </div>
                                                                    <ul className="list-group" {...getMenuProps()}>
                                                                        {
                                                                            isOpen ? 
                                                                            this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                                .map((item, index) => (
                                                                                <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                    style: {
                                                                                        backgroundColor:
                                                                                        highlightedIndex === index ? 'lightgray' : 'white',
                                                                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                    },
                                                                                    })}
                                                                                >
                                                                                    {item.value}
                                                                                </li>
                                                                            ))
                                                                            : null
                                                                        }
                                                                    </ul>
                                                                </div>
                                                                )}
                                                            </Downshift>
                                                        </div>
                                                        :

                                                        this.state.data.Schedulable__r.Electrical_Type__c === 'Miscellaneous Electrical' && field.name === 'Type_of_Transfer_Switch__c' ?

                                                        <div>
                                                            <br></br>
                                                            <h1>Transfer Switch Misc. Questions</h1>
                                                            <label className="text-white">Today's Crew Members (Select All):</label>
                                                            <select name="installCrew" multiple id="installCrew" onChange={(event) => this.updateFieldTeam(event.target.selectedOptions)}>
                                                                { this.state.fieldTeamPicklist.map((option) => <option key={option.id} value={option.Payroll_ID__c}>{option.Name}</option>) }
                                                            </select>

                                                            <label className="text-white pt-3">Or Add Additional Tech Not On Crew:</label>
                                                            <Downshift 
                                                                onChange={selection => this.setState({addidtionalTech: selection.id})}
                                                                itemToString={item => (item ? item.value : '')}
                                                            >
                                                                {({
                                                                    getInputProps,
                                                                    getItemProps,
                                                                    getLabelProps,
                                                                    getMenuProps,
                                                                    isOpen,
                                                                    inputValue,
                                                                    highlightedIndex,
                                                                    selectedItem,
                                                                    getToggleButtonProps
                                                                }) => (
                                                                <div>
                                                                    <div className="w-72 flex flex-col gap-1">
                                                                        <div className="flex bg-white gap-0.5">
                                                                            <input className='form-control' placeholder="Type Tech's Name" {...getInputProps()} />
                                                                        </div>
                                                                    </div>
                                                                    <ul className="list-group" {...getMenuProps()}>
                                                                        {
                                                                            isOpen ? 
                                                                            this.state.activeFieldPersonnel.filter(item => !inputValue || (item.value).toLowerCase().includes(inputValue) || item.value.includes(inputValue))
                                                                                .map((item, index) => (
                                                                                <li className="list-group-item" {...getItemProps({key: item.value, index, item,
                                                                                    style: {
                                                                                        backgroundColor:
                                                                                        highlightedIndex === index ? 'lightgray' : 'white',
                                                                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                                                                    },
                                                                                    })}
                                                                                >
                                                                                    {item.value}
                                                                                </li>
                                                                            ))
                                                                            : null
                                                                        }
                                                                    </ul>
                                                                </div>
                                                                )}
                                                            </Downshift>
                                                        </div>
                                                        :
                                                        ''
                                                    :
                                                    ''
                                                }
                                                {/* ----- single-line text, url, number: ----- */}
                                                <div className={
                                                    field.type === 'text' ||
                                                    field.type === 'url' ||
                                                    // number type if Roof Tilt:
                                                    (field.type === 'num' && field.name === 'Roof_Tilt__c')
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                        { field.label }
                                                        <span className={ field.required ? '' : 'd-none' }> *</span>
                                                    </label>
                                                    <div className="col-sm-7 col-12">
                                                        <input
                                                            className="input"
                                                            type="text"
                                                            value={field.value ? field.value : ''}
                                                            placeholder={field.label}
                                                            onChange={(event) => this.fieldOnChange(field.name, event.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ----- Text input with limited characters: ----- */}
                                                <div className={
                                                    field.type === 'textLimited' ?
                                                    'row mt-3'
                                                    :
                                                    'd-none'
                                                }>
                                                    <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                        { field.label }
                                                        <span className={ field.required ? '' : 'd-none' }> *</span>
                                                    </label>
                                                    <div className="col-sm-7 col-12">
                                                        <input
                                                            className="input"
                                                            type="text"
                                                            maxlength="14"
                                                            value={field.value ? field.value : ''}
                                                            placeholder={field.label}
                                                            onChange={(event) => this.fieldOnChange(field.name, event.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ----- number (non-Roof Tilt): ----- */}
                                                <div className={
                                                    field.type === 'num' &&
                                                    field.name !== 'Roof_Tilt__c'
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                        { field.label }
                                                        <span className={ field.required ? '' : 'd-none' }> *</span>
                                                    </label>
                                                    <div className="col-sm-7 col-12">
                                                        <input
                                                            className="input"
                                                            type="number"
                                                            pattern="^\d+\.(?=\d{1,3}$)\d+|^\d+(?!\.)$"
                                                            value={field.value ? field.value : ''}
                                                            placeholder={field.label}
                                                            onChange={(event) => this.fieldOnChange(field.name, event.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ----- textarea and textareawide: ----- */}
                                                <div className={
                                                    field.type === 'textarea' ||
                                                    field.type === 'textareawide'
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <div className="col-sm-5 col-12">
                                                        <label className="text-white m-0 py-2 d-inline-block">
                                                            <div>
                                                                { field.label }
                                                                <span className={ field.required ? 'text-sm' : 'd-none' }> *</span>
                                                            </div>
                                                            <div className={field.subtext ? 'text-sm' : 'd-none'}>
                                                                { field.subtext }
                                                            </div>
                                                        </label>
                                                    </div>
                                                    <div className="col-sm-7 col-12">
                                                        <textarea
                                                            rows="8"
                                                            name={field.name}
                                                            placeholder={field.label}
                                                            value={field.value ? field.value : ''}
                                                            onChange={(event) => this.fieldOnChange(field.name, event.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ----- checkbox: ----- */}
                                                <div className={
                                                    field.type === 'checkbox'
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-white col-sm-8 col-12 m-0 py-2">
                                                        { field.label }
                                                        <span className={ field.required ? '' : 'd-none' }> *</span>
                                                    </label>
                                                    <div className="col-sm-4 col-12">
                                                        <input
                                                            type="checkbox"
                                                            defaultChecked={field.value ? true : false}
                                                            onChange={(event) => this.fieldOnChange(field.name, !event.target.defaultChecked)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ----- picklist: ----- */}
                                                <div className={
                                                    (field.type && field.type.includes('Pick') && field.name !== 'Did_the_customer_sign_the_NEM_form__c' ) ? 'row mt-3' : 'd-none'
                                                }>
                                                    <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                        { field.label }
                                                        <span className={field.labelAddOn ? '' : 'd-none'}> {field.labelAddOn}</span>
                                                        <span className={field.required ? '' : 'd-none' }> *</span>
                                                    </label>
                                                    <div className="col-sm-7 col-12">
                                                        <select className="select"
                                                                name={field.name}
                                                                value={field.value ? field.value : ''}
                                                                onChange={
                                                                    (this.state.jobType === 'Site Survey' && field.name === 'Site_Survey_Outcome__c')
                                                                        ?
                                                                        (event) => this.siteSurveyFieldOnChange(field.name, event.target.value)
                                                                        :
                                                                        (this.state.jobType === 'Install' && field.name === 'Install_Outcome__c')
                                                                            ?
                                                                            (event) => this.installFieldOnChange(field.name, event.target.value)
                                                                            :
                                                                            (this.state.jobType === 'Inspection' && field.name === 'Inspection_Outcome__c')
                                                                                ?
                                                                                (event) => this.inspectionFieldOnChange(field.name, event.target.value)
                                                                                :
                                                                                    (event) => this.fieldOnChange(field.name, event.target.value)
                                                                }
                                                        >
                                                            <option hidden value=""> </option>
                                                            {
                                                                // yesNoPick:
                                                                (field.type === 'yesNoPick' || field.type === 'yesNoOtherPick') ?
                                                                    <>
                                                                        <option value="Yes">Yes</option>
                                                                        <option value="No">No</option>
                                                                    </>
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // passFailNAPick:
                                                                field.type === 'passFailNAPick' ?
                                                                    <>
                                                                        <option value="Pass">Pass</option>
                                                                        <option value="Fail">Fail</option>
                                                                        <option value="N/A">N/A</option>
                                                                    </>
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // yesNoNAPick:
                                                                field.type === 'yesNoNAPick' ?
                                                                    <>
                                                                        <option value="Yes">Yes</option>
                                                                        <option value="No">No</option>
                                                                        <option value="N/A">N/A</option>
                                                                    </>
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // passFailOtherPick:
                                                                (field.type === 'passFailOtherPick' || field.type === 'passFailOtherPickRequired') ?
                                                                    <>
                                                                        <option value="Pass">Pass</option>
                                                                        <option value="Fail">Fail</option>
                                                                        <option value="Other">Other</option>
                                                                    </>
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // panel pick:
                                                                field.type === 'panelPick' ?
                                                                    this.state.panelsOptions.map((panel, index) => {
                                                                        return (
                                                                            <option key={index} value={panel.value}>{panel.label}</option>
                                                                        );
                                                                    })
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // electricians picker:
                                                                field.type === 'panelPick' ?
                                                                    this.state.panelsOptions.map((panel, index) => {
                                                                        return (
                                                                            <option key={index} value={panel.value}>{panel.label}</option>
                                                                        );
                                                                    })
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // electriciansPick:
                                                                field.type === 'electriciansPick' ?
                                                                    this.state.electricianOptions.map((electrician, index) => {
                                                                        return (
                                                                            <option key={index} value={electrician.value}>{electrician.label}</option>
                                                                        );
                                                                    })
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // fields where there is an `options` property (but we will exclude the field if it include `ssOutcomeOptions`):
                                                                field.options && !field.ssOutcomeOptions ?
                                                                    field.options.map((option, index) => {
                                                                        return (
                                                                            <option key={index} value={option.value}>{option.label}</option>
                                                                        );
                                                                    })
                                                                    :
                                                                    null
                                                            }
                                                            {
                                                                // field with `ssOutcomeOptions` property:
                                                                field.ssOutcomeOptions ?
                                                                    field.ssOutcomeOptions.map((option, index) => {
                                                                        return (
                                                                            <option key={index} value={option.value}>{option.label}</option>
                                                                        );
                                                                    })
                                                                    :
                                                                    null
                                                            }
                                                        </select>
                                                    </div>
                                                </div> {/* /picklist */}

                                                {/* ----- optional output message: ----- */}
                                                <div className={
                                                    field.type === 'passFailOtherPickRequired' &&
                                                    !this.state.data['Inspection_Outcome__c'] &&
                                                    this.state.jobType === 'Inspection'
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <div className="col-sm-7 offset-sm-5 col-12">This is required to save.</div>
                                                </div>

                                                <div className={field.displayMessageWhen === field.value ? 'row' : 'd-none'}>
                                                    <div className="col-sm-7 offset-sm-5 col-12 py-1">{this.FIELD_EXPLANATION_MESSAGE}</div>
                                                </div>

                                                {/* ----- custom: ----- */}
                                                <div className={
                                                    field.name === 'Did_the_customer_sign_the_NEM_form__c' &&
                                                    this.state.data['Schedulable__r']['NEM_Signed_Date__c'] === null
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="col-sm-5 col-12 m-0 py-2">{ field.label } </label>
                                                    <div className="col-sm-7 col-12">
                                                        <select
                                                            className="select"
                                                            name={field.name}
                                                            value={field.value ? field.value : ''}
                                                            onChange={(event) => this.fieldOnChange(field.name, event.target.value)}
                                                        >
                                                            <option hidden value=""> </option>
                                                            <option value="Yes">Yes</option>
                                                            <option value="No">No</option>
                                                        </select>

                                                        {/* <div className={field.displayMessageWhen === field.value ? 'py-1' : 'd-none'}>
                                                            {this.FIELD_EXPLANATION_MESSAGE}
                                                        </div> */}
                                                    </div>
                                                </div>

                                                {/* ----- Site Survey: ----- */}
                                                {/* Section Rendered for 'Is_There_Existing_Solar__c' Sub Questions in Edit Mode: */}
                                                <div className={
                                                    this.state.jobType === 'Site Survey' &&
                                                    field.name === 'Is_There_Existing_Solar__c' &&
                                                    field.value === 'Yes'
                                                        ?
                                                        ''
                                                        :
                                                        'd-none'
                                                }>
                                                    {
                                                        this.state.existingSolarFields.map(solarField => {
                                                            if (solarField.type.includes('Pick')) {
                                                                return (
                                                                    <div className="row mt-3">
                                                                        <label className="col-sm-5 col-12 pl-5 m-0 py-2">
                                                                            { solarField.label }
                                                                            <span className={ solarField.required ? '' : 'd-none' }> *</span>
                                                                        </label>
                                                                        <div className="col-sm-7 col-12">
                                                                            <select
                                                                                className='select'
                                                                                onChange={(event) => this.existingSolarFieldOnChange(solarField.name, event.target.value)}
                                                                                value={solarField.value}
                                                                            >
                                                                                <option hidden value=""> </option>
                                                                                {
                                                                                    solarField.type === 'yesNoPick' ?
                                                                                        <>
                                                                                            <option value="Yes">Yes</option>
                                                                                            <option value="No">No</option>
                                                                                        </>
                                                                                        : null
                                                                                }
                                                                                {
                                                                                    solarField.type === 'passFailNAPick' ?
                                                                                        <>
                                                                                            <option value="Pass">Pass</option>
                                                                                            <option value="Fail">Fail</option>
                                                                                            <option value="N/A">N/A</option>
                                                                                        </>
                                                                                        : null
                                                                                }
                                                                                {
                                                                                    solarField.type === 'yesNoNAPick' ?
                                                                                        <>
                                                                                            <option value="Yes">Yes</option>
                                                                                            <option value="No">No</option>
                                                                                            <option value="N/A">N/A</option>
                                                                                        </>
                                                                                        : null
                                                                                }
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        })
                                                    }
                                                </div>

                                                {/* Section Rendered for 'Site_Survey_Outcome__c' Sub Questions in Edit Mode: */}
                                                <div className={
                                                    this.state.jobType === 'Site Survey' &&
                                                    field.name === 'Site_Survey_Outcome__c' ?
                                                        ''
                                                        :
                                                        'd-none'
                                                }>
                                                    {
                                                        this.state.surveyCompletionFields.map(surveyCompletionField => {
                                                            return (
                                                                <div className={
                                                                    surveyCompletionField.type.includes('Pick') &&
                                                                    // we only show the follow-up question ('ssOutcome') based on the selected value for `Site_Survey_Outcome__c` in `this.state.editFields`:
                                                                    surveyCompletionField['ssOutcome'] === field.value
                                                                        ?
                                                                        "row mt-3"
                                                                        :
                                                                        'd-none'
                                                                }>
                                                                    <label className="col-sm-5 col-12 m-0 py-2">
                                                                        { surveyCompletionField.label }
                                                                        <span className={ surveyCompletionField.required ? '' : 'd-none' }> *</span>
                                                                    </label>
                                                                    <div className="col-sm-7 col-12">
                                                                        <select
                                                                            className="select"
                                                                            onChange={(event) => this.surveyCompletionFieldsOnChange(
                                                                                surveyCompletionField.name,
                                                                                event.target.value)}
                                                                            value={surveyCompletionField.value}
                                                                        >
                                                                            <option hidden value=""> </option>
                                                                            {
                                                                                surveyCompletionField.type === 'yesNoPick' ?
                                                                                    <>
                                                                                        <option value="Yes">Yes</option>
                                                                                        <option value="No">No</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                surveyCompletionField.type === 'passFailNAPick' ?
                                                                                    <>
                                                                                        <option value="Pass">Pass</option>
                                                                                        <option value="Fail">Fail</option>
                                                                                        <option value="N/A">N/A</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                surveyCompletionField.type === 'yesNoNAPick' ?
                                                                                    <>
                                                                                        <option value="Yes">Yes</option>
                                                                                        <option value="No">No</option>
                                                                                        <option value="N/A">N/A</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                surveyCompletionField.options ?
                                                                                    surveyCompletionField.options.map((option, index) => {
                                                                                        return (
                                                                                            <option key={index} value={option.value}>{option.label}</option>
                                                                                        );
                                                                                    })
                                                                                    :
                                                                                    null
                                                                            }
                                                                        </select>
                                                                        <div className={field.displayMessageWhen === field.value ? 'py-1' : 'd-none'}>
                                                                            {this.FIELD_EXPLANATION_MESSAGE}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>

                                                {/* ----- subField (optional input fields): -----  */}
                                                {
                                                    // we use ternary expression rather than CSS classes because `field.subField` is an optional property and we don't want to do a check every time we reference in the markup:
                                                    field.showSubFieldIf === field.value
                                                        ?
                                                        (
                                                            <div className="row mt-3">
                                                                <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                                    { field.subField.label }
                                                                    <span className={ field.subField.required ? '' : 'd-none' }> *</span>
                                                                    <div className={field.subField.subtext ? 'text-md text-lightgrey' : 'd-none'}>
                                                                        { field.subField.subtext }
                                                                    </div>
                                                                </label>

                                                                <div className={ field.subField.type === 'textarea' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        value={field.subField.value ? field.subField.value : ''}
                                                                        placeholder={field.subField.label}
                                                                        onChange={(event) => this.subFieldOnChange(
                                                                            field.name,
                                                                            field.subField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- Text input with limited characters: ----- */}
                                                                <div className={field.subField.type === 'textLimited' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        maxlength="18"
                                                                        value={field.subField.value ? field.subField.value : ''}
                                                                        placeholder={field.subField.label}
                                                                        onChange={(event) => this.subFieldOnChange(
                                                                            field.name,
                                                                            field.subField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- textareawide: ----- */}
                                                                <div className={field.subField.type === 'textareawide' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <textarea
                                                                        rows="8"
                                                                        name={field.subField.name}
                                                                        placeholder={field.subField.label}
                                                                        value={field.subField.value ? field.subField.value : ''}
                                                                        onChange={(event) => this.subFieldOnChange(field.subField.name, event.target.value)}
                                                                    />
                                                                </div>

                                                                <div className={ field.subField.type === 'num' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="number"
                                                                        pattern="^\d+\.(?=\d{1,3}$)\d+|^\d+(?!\.)$"
                                                                        value={field.subField.value ? field.subField.value : ''}
                                                                        placeholder={field.subField.label}
                                                                        onChange={(event) => this.subFieldOnChange(
                                                                            field.name,
                                                                            field.subField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                <div className={field.subField.type.includes('Pick') ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <select
                                                                        className="select"
                                                                        value={field.subField.value}
                                                                        onChange={(event) => this.subFieldOnChange(
                                                                            field.name,
                                                                            field.subField.name,
                                                                            event.target.value)}
                                                                    >
                                                                        <option hidden value=""> </option>
                                                                        {
                                                                            field.subField.type === 'passFailNAPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.subField.type === 'yesNoPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.subField.type === 'yesNoNAPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.subField.type === 'passFailOtherPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.subField.type === 'yesNoOtherPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            // other/misc "picker" option type:
                                                                            (
                                                                                field.subField.options &&
                                                                                field.subField.type !== 'passFailNAPick' &&
                                                                                field.subField.type !== 'yesNoPick' &&
                                                                                field.subField.type !== 'yesNoNAPick' &&
                                                                                field.subField.type !== 'passFailOtherPick' &&
                                                                                field.subField.type !== 'yesNoOtherPick' &&
                                                                                field.subField.type !== 'multiSelectList'
                                                                            )
                                                                                ?
                                                                                field.subField.options.map((option, index) => {
                                                                                    return (
                                                                                        <option key={index} value={option.value}>{option.label}</option>
                                                                                    );
                                                                                })
                                                                                :
                                                                                null
                                                                        }
                                                                    </select>
                                                                    <div className={field.subField.displayMessageWhen === field.subField.value ? 'py-1' : 'd-none'}>
                                                                        {/* {this.FIELD_EXPLANATION_MESSAGE} */}
                                                                    </div>
                                                                </div>

                                                                <div className={ field.subField.type === 'multiSelectList' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <div className={css.FieldJob}>
                                                                    <select
                                                                        className="select"
                                                                        onChange={(event) => this.multipickFieldOnChange(
                                                                            field.name,
                                                                            field.subField.name,
                                                                            event.target.selectedOptions)}
                                                                        multiple
                                                                    >
                                                                        <option hidden value=""> </option>
                                                                        {
                                                                            (
                                                                                field.subField.options
                                                                            ) ?
                                                                            field.subField.options.map((option, index) => {
                                                                                return (
                                                                                    <option key={index} value={option.value}>{option.label}</option>
                                                                                );
                                                                            })
                                                                            :
                                                                            null
                                                                        }
                                                                    </select>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        )
                                                        :
                                                        null
                                                }
                                                {/* ----- secondary subField (optional input fields): -----  */}
                                                {
                                                    // we use ternary expression rather than CSS classes because `field.secondSubField` is an optional property and we don't want to do a check every time we reference in the markup:
                                                    field.showSecondSubFieldIf === field.value
                                                        ?
                                                        (
                                                            <div className="row mt-3">
                                                                <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                                    { field.secondSubField.label }
                                                                    <span className={ field.secondSubField.required ? '' : 'd-none' }> *</span>
                                                                </label>

                                                                <div className={ field.secondSubField.type === 'textarea' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        value={field.secondSubField.value ? field.secondSubField.value : ''}
                                                                        placeholder={field.secondSubField.label}
                                                                        onChange={(event) => this.secondarySubFieldOnChange(
                                                                            field.name,
                                                                            field.secondSubField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>
                                                                <div className={ field.secondSubField.type === 'num' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        pattern="^\d+\.(?=\d{1,3}$)\d+|^\d+(?!\.)$"
                                                                        value={field.secondSubField.value ? field.secondSubField.value : ''}
                                                                        placeholder={field.secondSubField.label}
                                                                        onChange={(event) => this.secondarySubFieldOnChange(
                                                                            field.name,
                                                                            field.secondSubField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                <div className={field.secondSubField.type.includes('Pick') ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <select
                                                                        className="select"
                                                                        value={field.secondSubField.value}
                                                                        onChange={(event) => this.secondarySubFieldOnChange(
                                                                            field.name,
                                                                            field.secondSubField.name,
                                                                            event.target.value)}
                                                                    >
                                                                        <option hidden value=""> </option>
                                                                        {
                                                                            field.secondSubField.type === 'passFailNAPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.secondSubField.type === 'yesNoPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.secondSubField.type === 'yesNoNAPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.secondSubField.type === 'passFailOtherPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.secondSubField.type === 'yesNoOtherPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            // other/misc "picker" option type:
                                                                            (
                                                                                field.secondSubField.options &&
                                                                                field.secondSubField.type !== 'passFailNAPick' &&
                                                                                field.secondSubField.type !== 'yesNoPick' &&
                                                                                field.secondSubField.type !== 'yesNoNAPick' &&
                                                                                field.secondSubField.type !== 'passFailOtherPick' &&
                                                                                field.secondSubField.type !== 'yesNoOtherPick'
                                                                            )
                                                                                ?
                                                                                field.secondSubField.options.map((option, index) => {
                                                                                    return (
                                                                                        <option key={index} value={option.value}>{option.label}</option>
                                                                                    );
                                                                                })
                                                                                :
                                                                                null
                                                                        }
                                                                    </select>
                                                                    <div className={field.secondSubField.displayMessageWhen === field.secondSubField.value ? 'py-1' : 'd-none'}>
                                                                        {/* {this.FIELD_EXPLANATION_MESSAGE} */}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                        :
                                                        null
                                                }

                                                {/* More Subfields are now being used on field jobs that display conditionally. Making a separate component that builds out the subfields
                                                might be more beneficial in the future instead of repeating these blocks. 
                                                Also only made the type of subfield needed for the time being and didn't repeat them all for third, fourth, and fifth subfields */}

                                                {/* ----- third subField (optional input fields): -----  */}
                                                {
                                                    // we use ternary expression rather than CSS classes because `field.thirdSubField` is an optional property and we don't want to do a check every time we reference in the markup:
                                                    field.showThirdSubFieldIf === field.value
                                                        ?
                                                        (
                                                            <div className="row mt-3">
                                                                <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                                    { field.thirdSubField.label }
                                                                    <span className={ field.thirdSubField.required ? '' : 'd-none' }> *</span>
                                                                    <div className={field.thirdSubField.subtext ? 'text-md text-lightgrey' : 'd-none'}>
                                                                        { field.thirdSubField.subtext }
                                                                    </div>
                                                                </label>

                                                                <div className={ field.thirdSubField.type === 'textarea' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        value={field.thirdSubField.value ? field.thirdSubField.value : ''}
                                                                        placeholder={field.thirdSubField.label}
                                                                        onChange={(event) => this.thirdSubFieldOnChange(
                                                                            field.name,
                                                                            field.thirdSubField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- Pick list: ----- */}
                                                                <div className={ field.thirdSubField.type.includes('Pick') ?  'col-sm-7 col-12' : 'd-none'}>
                                                                    {
                                                                        field.thirdSubField.options
                                                                        ?
                                                                        <select
                                                                            className="select"
                                                                            value={field.thirdSubField.value}
                                                                            onChange={(event) => this.thirdSubFieldOnChange(
                                                                                field.name,
                                                                                field.thirdSubField.name,
                                                                                event.target.value)}
                                                                        >
                                                                            <option hidden value=""> </option>
                                                                            {
                                                                                field.thirdSubField.type === 'passFailNAPick' ?
                                                                                    <>
                                                                                        <option value="Pass">Pass</option>
                                                                                        <option value="Fail">Fail</option>
                                                                                        <option value="N/A">N/A</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                field.thirdSubField.type === 'yesNoPick' ?
                                                                                    <>
                                                                                        <option value="Yes">Yes</option>
                                                                                        <option value="No">No</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                field.thirdSubField.type === 'yesNoNAPick' ?
                                                                                    <>
                                                                                        <option value="Yes">Yes</option>
                                                                                        <option value="No">No</option>
                                                                                        <option value="N/A">N/A</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                field.thirdSubField.type === 'passFailOtherPick' ?
                                                                                    <>
                                                                                        <option value="Pass">Pass</option>
                                                                                        <option value="Fail">Fail</option>
                                                                                        <option value="Other">Other</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                field.thirdSubField.type === 'yesNoOtherPick' ?
                                                                                    <>
                                                                                        <option value="Yes">Yes</option>
                                                                                        <option value="No">No</option>
                                                                                        <option value="Other">Other</option>
                                                                                    </>
                                                                                    : null
                                                                            }
                                                                            {
                                                                                // other/misc "picker" option type:
                                                                                (
                                                                                    field.thirdSubField.options &&
                                                                                    field.thirdSubField.type !== 'passFailNAPick' &&
                                                                                    field.thirdSubField.type !== 'yesNoPick' &&
                                                                                    field.thirdSubField.type !== 'yesNoNAPick' &&
                                                                                    field.thirdSubField.type !== 'passFailOtherPick' &&
                                                                                    field.thirdSubField.type !== 'yesNoOtherPick'
                                                                                )
                                                                                    ?
                                                                                    field.thirdSubField.options.map((option, index) => {
                                                                                        return (
                                                                                            <option key={index} value={option.value}>{option.label}</option>
                                                                                        );
                                                                                    })
                                                                                    :
                                                                                    null
                                                                            }
                                                                        </select>
                                                                        :
                                                                        null
                                                                    }
                                                                </div>

                                                                {/* ----- multipick list: ----- */}
                                                                <div className={ field.thirdSubField.type === 'multiSelectList' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    {
                                                                        field.thirdSubField.options
                                                                        ?
                                                                        <div className={css.FieldJob}>
                                                                        <select
                                                                            className="select"
                                                                            onChange={(event) => this.thirdSubFieldOnChangeMultiPick(
                                                                                field.name,
                                                                                field.thirdSubField.name,
                                                                                event.target.selectedOptions)}
                                                                            multiple
                                                                        >
                                                                            <option hidden value=""> </option>
                                                                            {
                                                                                field.thirdSubField.options.map((option, index) => {
                                                                                    return (
                                                                                        <option key={index} value={option.value}>{option.label}</option>
                                                                                    );
                                                                                })
                                                                            }
                                                                        </select>
                                                                        </div>
                                                                        :
                                                                        null
                                                                    }
                                                                </div>
                                                                <div className={field.thirdSubField.displayMessageWhen === field.thirdSubField.value ? 'py-1' : 'd-none'}>
                                                                    {this.FIELD_EXPLANATION_MESSAGE}
                                                                </div>
                                                            </div>
                                                        )
                                                        :
                                                        null
                                                }
                                                {/* ----- fourth subField (optional input fields): -----  */}
                                                {
                                                    // we use ternary expression rather than CSS classes because `field.fourthSubField` is an optional property and we don't want to do a check every time we reference in the markup:
                                                    field.showFourthSubFieldIf === field.value
                                                        ?
                                                        (
                                                            <div className="row mt-3">
                                                                <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                                    { field.fourthSubField.label }
                                                                    <span className={ field.fourthSubField.required ? '' : 'd-none' }> *</span>
                                                                    <div className={field.fourthSubField.subtext ? 'text-md text-lightgrey' : 'd-none'}>
                                                                        { field.fourthSubField.subtext }
                                                                    </div>
                                                                </label>

                                                                {/* ----- textarea: ----- */}
                                                                <div className={ field.fourthSubField.type === 'textarea' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        value={field.fourthSubField.value ? field.fourthSubField.value : ''}
                                                                        placeholder={field.fourthSubField.label}
                                                                        onChange={(event) => this.fourthSubFieldOnChange(
                                                                            field.name,
                                                                            field.fourthSubField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- textareawide: ----- */}
                                                                <div className={field.fourthSubField.type === 'textareawide' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <textarea
                                                                        rows="8"
                                                                        name={field.fourthSubField.name}
                                                                        placeholder={field.fourthSubField.label}
                                                                        value={field.fourthSubField.value ? field.fourthSubField.value : ''}
                                                                        onChange={(event) => this.fourthSubFieldOnChange(field.name, field.fourthSubField.name, event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- PickList: ----- */}
                                                                <div className={field.fourthSubField.type.includes('Pick') ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <select
                                                                        className="select"
                                                                        value={field.fourthSubField.value}
                                                                        onChange={(event) => this.fourthSubFieldOnChange(
                                                                            field.name,
                                                                            field.fourthSubField.name,
                                                                            event.target.value)}
                                                                    >
                                                                        <option hidden value=""> </option>
                                                                        {
                                                                            field.fourthSubField.type === 'passFailNAPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.fourthSubField.type === 'yesNoPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.fourthSubField.type === 'yesNoNAPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="N/A">N/A</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.fourthSubField.type === 'passFailOtherPick' ?
                                                                                <>
                                                                                    <option value="Pass">Pass</option>
                                                                                    <option value="Fail">Fail</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            field.fourthSubField.type === 'yesNoOtherPick' ?
                                                                                <>
                                                                                    <option value="Yes">Yes</option>
                                                                                    <option value="No">No</option>
                                                                                    <option value="Other">Other</option>
                                                                                </>
                                                                                : null
                                                                        }
                                                                        {
                                                                            // other/misc "picker" option type:
                                                                            (
                                                                                field.fourthSubField.options &&
                                                                                field.fourthSubField.type !== 'passFailNAPick' &&
                                                                                field.fourthSubField.type !== 'yesNoPick' &&
                                                                                field.fourthSubField.type !== 'yesNoNAPick' &&
                                                                                field.fourthSubField.type !== 'passFailOtherPick' &&
                                                                                field.fourthSubField.type !== 'yesNoOtherPick'
                                                                            )
                                                                                ?
                                                                                field.fourthSubField.options.map((option, index) => {
                                                                                    return (
                                                                                        <option key={index} value={option.value}>{option.label}</option>
                                                                                    );
                                                                                })
                                                                                :
                                                                                null
                                                                        }
                                                                    </select>
                                                                    <div className={field.fourthSubField.displayMessageWhen === field.fourthSubField.value ? 'py-1' : 'd-none'}>
                                                                        {this.FIELD_EXPLANATION_MESSAGE}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                        :
                                                        null
                                                }
                                                {/* ----- fifth subField (optional input fields): -----  */}
                                                {
                                                    // we use ternary expression rather than CSS classes because `field.fifthSubField` is an optional property and we don't want to do a check every time we reference in the markup:
                                                    field.showFifthSubFieldIf === field.value
                                                        ?
                                                        (
                                                            <div className="row mt-3">
                                                                <label className="text-white col-sm-5 col-12 m-0 py-2">
                                                                    { field.fifthSubField.label }
                                                                    <span className={ field.fifthSubField.required ? '' : 'd-none' }> *</span>
                                                                    <div className={field.fifthSubField.subtext ? 'text-md text-lightgrey' : 'd-none'}>
                                                                        { field.fifthSubField.subtext }
                                                                    </div>
                                                                </label>

                                                                {/* ----- textarea: ----- */}
                                                                <div className={ field.fifthSubField.type === 'textarea' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <input
                                                                        className="input"
                                                                        type="text"
                                                                        value={field.fifthSubField.value ? field.fifthSubField.value : ''}
                                                                        placeholder={field.fifthSubField.label}
                                                                        onChange={(event) => this.fifthSubFieldOnChange(
                                                                            field.name,
                                                                            field.fifthSubField.name,
                                                                            event.target.value)}
                                                                    />
                                                                </div>

                                                                {/* ----- textareawide: ----- */}
                                                                <div className={field.fifthSubField.type === 'textareawide' ? 'col-sm-7 col-12' : 'd-none'}>
                                                                    <textarea
                                                                        rows="8"
                                                                        name={field.fifthSubField.name}
                                                                        placeholder={field.fifthSubField.label}
                                                                        value={field.fifthSubField.value ? field.fifthSubField.value : ''}
                                                                        onChange={(event) => this.fifthSubFieldOnChange(field.fifthSubField.name, event.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )
                                                        :
                                                        null
                                                }
                                            </div> // /map
                                        );
                                    })
                                }

                                {/* show 'end job' only after initial data fetch and if user has NOT clocked out: */}
                                <div className={this.state.editFields.length && !this.state.data.Clocked_End__c ? 'mt-3' : 'd-none'}>
                                    <button className="btn btn-outline w-100 text-md" type="button" onClick={() => this.completeJob()}>End Job</button>
                                </div>
                            </>
                        )
                        :
                        // ---------- If NOT Editing: ----------
                        (
                            <>
                                {
                                    this.state.editFields.map(field => {
                                        return (
                                            <div key={field.name}>
                                                {
                                                    field.name === 'Install_Outcome__c' ?
                                                    <h2>Roof Install Questions</h2>
                                                    :
                                                    ''
                                                }
                                                {
                                                    field.name === 'Does_the_equipment_installed_match_the_p__c' ?
                                                    <div>
                                                        <br></br>
                                                        <h2>Solar Electrical Questions</h2>
                                                    </div>
                                                    :
                                                    ''
                                                }
                                                {/* ----- single-line text, textarea, and number: ----- */}
                                                <div className={
                                                    (   field.type === 'text' ||
                                                        field.type === 'num' ||
                                                        field.type === 'textLimited' ||
                                                        field.type === 'textarea' ||
                                                        field.type === 'textareawide' ||
                                                        (field.name === 'Did_the_customer_sign_the_NEM_form__c' && this.state.data['Schedulable__r']['NEM_Signed_Date__c'] === null)
                                                    )
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ field.label }</label>
                                                    <div className="col-sm-7 col-12">
                                                        { field.value ? field.value : "---" }
                                                    </div>
                                                </div>

                                                {/* ----- checkbox ----- */}
                                                <div className={
                                                    (   
                                                        field.type === 'checkbox'
                                                    )
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ field.label }</label>
                                                    <div className="col-sm-7 col-12">
                                                        { field.value ? "Yes" : "No" }
                                                    </div>
                                                </div>

                                                {/* ----- url: ----- */}
                                                <div className={ (field.type === 'url' && field.name !== null) ? 'row mt-3' : 'd-none' }>
                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ field.label }</label>
                                                    {
                                                        field.value ?
                                                            (<a className="col-sm-7 col-12" href={formatLink(field.value)}>{ field.value }</a>)
                                                            :
                                                            (<span className="col-sm-7 col-12">---</span>)
                                                    }
                                                </div>

                                                {/* ----- picklist: ----- */}
                                                <div className={
                                                    field.type &&
                                                    field.type.includes('Pick') &&
                                                    field.name !== 'Did_the_customer_sign_the_NEM_form__c'
                                                        ?
                                                        'row mt-3'
                                                        :
                                                        'd-none'
                                                }>
                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ field.label }</label>
                                                    <div className="col-sm-7 col-12">
                                                        { field.value ? field.value : "---" }
                                                    </div>
                                                </div>

                                                {/* ----- Site Survey: ----- */}
                                                {/* ----- Section Rendered for 'Is_There_Existing_Solar__c' Sub Questions in View Only Mode: ----- */}
                                                <div className={
                                                    this.state.jobType === 'Site Survey' &&
                                                    field.name === 'Is_There_Existing_Solar__c' &&
                                                    this.state.data['Is_There_Existing_Solar__c'] === 'Yes'
                                                        ?
                                                        ''
                                                        :
                                                        'd-none'
                                                }>
                                                    {
                                                        this.state.existingSolarFields.map((questionField, index) => {
                                                            // condition guard against empty object (i.e. when data is still being fetched):
                                                            if (!this.state.data[questionField.name]) {
                                                                return <div key={index}> </div>;
                                                            }

                                                            return (
                                                                <div key={index} className={questionField.type.includes('Pick') ? 'row': 'd-none'}>
                                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ questionField.label }</label>
                                                                    <div className="col-sm-7 col-12">
                                                                        { this.state.data[questionField.name] ? this.state.data[questionField.name] : "---" }
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>

                                                {/* ----- Section Rendered for 'Site_Survey_Outcome__c' Sub Questions in View Only Mode: ----- */}
                                                <div className={
                                                    this.state.jobType === 'Site Survey' &&
                                                    field.name === 'Site_Survey_Outcome__c'
                                                        ?
                                                        ''
                                                        :
                                                        'd-none'
                                                }>
                                                    {
                                                        this.state.surveyCompletionFields.map((questionField, index) => {
                                                            // condition guard against empty object (i.e. when data is still being fetched):
                                                            if (!this.state.data[questionField.name]) {
                                                                return <div key={index}> </div>;
                                                            }

                                                            return (
                                                                <div key={index} className={
                                                                    questionField.type.includes('Pick') &&
                                                                    questionField['ssOutcome'] === this.state.data['Site_Survey_Outcome__c']
                                                                        ?
                                                                        'row mt-3':
                                                                        'd-none'
                                                                }>
                                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">{ questionField.label }</label>
                                                                    <div className="col-sm-7 col-12">
                                                                        { this.state.data[questionField.name] ? this.state.data[questionField.name] : "---" }
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>

                                                <div className={field.name === 'Completed_By__c' ? 'row mt-3' : 'd-none'}>
                                                    <label className="text-lightgrey col-sm-5 col-12 m-0">Assigned to Job</label>
                                                    <div className="col-sm-7 col-12">
                                                        { this.state.data['Completed_By__c'] ? this.state.data['Completed_By__r']['Name'] : "---" }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }

                                {/* show 'end job' only after initial data fetch and if user has NOT clocked out: */}
                                <div className={this.state.editFields.length && !this.state.data.Clocked_End__c ? 'mt-3' : 'd-none'}>
                                    <button className="btn btn-outline w-100 text-md" type="button" onClick={() => this.completeJob()}>End Job</button>
                                </div>
                            </>
                        )
                }

                {/* ----- CTA Buttons: ----- */}
                <div className="d-flex justify-content-between mt-5 mb-4">
                    {/* the ternary expression prevents user from clicking the link before `schedulableId` has been fetched: */}
                    <Link to={ this.schedulableId ? `/scheduled/${this.schedulableId}` : ''}>
                        <button className="btn btn-outline" type="button">Back</button>
                    </Link>
                    <div className={this.state.isEditing ? 'text-right' : 'd-none'}>
                        <button className="btn btn-outline" type="button" onClick={ () => this.setState({ isEditing: false }) }>Cancel</button>
                        <button className="btn btn-outline ml-2" type="button" onClick={() => this.saveFields()}>Save</button>
                    </div>
                    <div className={this.state.isEditing ? 'd-none' : 'text-right'}>
                        <a
                            href={formatLink(this.state.data.Schedulable__r ? this.state.data.Schedulable__r.Jobsite_Photos__c : '')}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <button className="btn btn-outline">Upload</button>
                        </a>
                        <button className="btn btn-outline ml-2" type="button" onClick={ () => this.setState({ isEditing: true }) }>Edit</button>
                    </div>
                </div>
                
            </div>
        );
    }
};


// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(FieldJob);
